import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CategoriesProvider } from "@/context/CategoriesContext";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import PortfolioGuide from "@/pages/PortfolioGuide";
import CastingGuide from "@/pages/CastingGuide";
import Models from "@/pages/Models";
import ModelDetail from "@/pages/ModelDetail";
import Castings from "@/pages/Castings";
import CastingDetail from "@/pages/CastingDetail";
import Auth from "@/pages/Auth";
import ModelDashboard from "@/pages/ModelDashboard";
import EmployerDashboard from "@/pages/EmployerDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import { Terms, Privacy, RefundPolicy, AffiliatePolicy } from "@/pages/Legal";
import CompCard from "@/pages/CompCard";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import CompleteProfile from "@/pages/CompleteProfile";
import ResetPassword from "@/pages/ResetPassword";
import OfferClaim from "@/pages/OfferClaim";
import ReferralRedirect from "@/pages/ReferralRedirect";

function Dashboard() {
  const { user } = useAuth();
  if (user === undefined) return <div className="px-6 sm:px-10 py-32 overline-label">Opening the atelier…</div>;
  if (!user) return <Navigate to="/auth" replace />;
  if (!user.role) return <Navigate to="/auth/complete" replace />;
  if (user.role === "admin") return <AdminDashboard />;
  if (user.role === "employer") return <EmployerDashboard />;
  return <ModelDashboard />;
}

function ScrollReset() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <CategoriesProvider>
        <BrowserRouter>
          <ScrollReset />
          <Layout>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/models" element={<Models />} />
              <Route path="/models/:id" element={<ModelDetail />} />
              <Route path="/models/:id/card" element={<CompCard />} />
              <Route path="/castings" element={<Castings />} />
              <Route path="/castings/:id" element={<CastingDetail />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/refund" element={<RefundPolicy />} />
              <Route path="/affiliate-policy" element={<AffiliatePolicy />} />
              <Route path="/portfolio-guide" element={<PortfolioGuide />} />
              <Route path="/casting-guide" element={<CastingGuide />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/complete" element={<CompleteProfile />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/offer/claim" element={<OfferClaim />} />
              <Route path="/r/:code" element={<ReferralRedirect />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Layout>
        </BrowserRouter>
        </CategoriesProvider>
        <Toaster theme="dark" position="bottom-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
